/* eslint-disable react/no-unknown-property */
import * as React from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

type _Props = {
  color: string;
  depth?: number;
  height?: number;
  onClick?: () => void;
  position: [number, number, number];
  title: string;
  width?: number;
};

const ThreeJSCard = ({ color, depth = 0.1, height = 1, onClick, position, title, width = 1.5 }: _Props) => {
  const mesh = React.useRef<THREE.Mesh>(null);
  const [isHovered, setIsHovered] = React.useState(false);

  useFrame(() => {
    if (mesh.current) {
      // Scale effect when hovered
      mesh.current.scale.x = THREE.MathUtils.lerp(mesh.current.scale.x, isHovered ? 1.1 : 1, 0.1);
      mesh.current.scale.y = THREE.MathUtils.lerp(mesh.current.scale.y, isHovered ? 1.1 : 1, 0.1);
      mesh.current.scale.z = THREE.MathUtils.lerp(mesh.current.scale.z, isHovered ? 1.1 : 1, 0.1);
    }
  });

  // Calculate optimal font size based on card dimensions and text length
  const fontSize = React.useMemo(() => {
    // Base size relative to card width
    const baseSize = width * 0.15;

    // Adjust based on text length - longer text gets smaller font
    const lengthFactor = Math.max(0.5, 1 - title.length / 30);

    // Ensure the text fits within the card's width
    const widthConstrainedSize = width / Math.max(5, title.length / 2);

    // Take the minimum of the calculated sizes to ensure it fits
    return Math.min(baseSize * lengthFactor, widthConstrainedSize, height * 0.3);
  }, [title, width, height]);

  return (
    <group
      onClick={onClick}
      onPointerOut={() => setIsHovered(false)}
      onPointerOver={() => setIsHovered(true)}
      position={position}
    >
      <mesh ref={mesh}>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial color={color} metalness={0.3} opacity={0.9} roughness={0.4} transparent />
      </mesh>
      <Text
        anchorX="center"
        anchorY="middle"
        color="#000000"
        fontSize={fontSize}
        maxWidth={width * 0.9}
        position={[0, 0, depth / 2 + 0.01]}
      >
        {title}
      </Text>
    </group>
  );
};

export default ThreeJSCard;
